import React, {useEffect, useState, useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import {isNull} from 'lodash';
import {labels, currentResource, normalize} from '../../../../../../util/utils';
import {actionIcDelete24px} from '@ey/motif-react-wrapper/assets/icons/index';
import {AccountType, clientSideEventNames} from '../../../../../../util/uiconstants';
import {useEngagementId} from '../../../../../../util/customHooks';
import {getSliceById} from '../../../../../../selectors/Ares/selectors';
import AssertionSelect from './AssertionSelect';
import EllipsesControl from '../../../../EllipsesControl/EllipsesControl';
import SelectDropdown from '../../../../SelectDropdown/SelectDropdown';
import ProgressBar from '../../../../BaseComponentWrappers/MotifProgressBar';
import IconButton from '../../../../BaseComponentWrappers/MotifIconButton';
import CreateEditAccountModalWrapper from '../../../../../Common/EntityCrud/CreateEditAccountModalWrapper';
import AccountExecutionType from '../../../../Accounts/AccountExecutionType/AccountExecutionType';
import {createAssertionRiskRelation, deleteAssertionRiskRelation} from '../../../../../../actions/Risk/riskactions';
import Button from '../../../../BaseComponentWrappers/MotifButton';
import {updateAccount} from '../../../../../../actions/Account/accountactions';

export default function AccountListItem(props) {
	const {risk, accountHash, accountId, accounts, accountListOption, getBodiesBySectionId, isDisabled} = props;

	const [selectedAccount, setSelectedAccount] = useState(null),
		[selectedStatementType, setSelectedStatementType] = useState(null),
		[selectedAssertions, setSelectedAssertions] = useState(null),
		[availableAssertionsBasedOnStatementType, setAvailableAssertionsBasedOnStatementType] = useState([]),
		[isLoading, setIsLoading] = useState(false),
		[isAssertionsDropdownDisabled, setIsAssertionsDropdownDisabled] = useState(true),
		[isDeleteDisabled, setIsDeleteDisabled] = useState(true),
		[isAccountDropdownDisabled, setIsAccountDropdownDisabled] = useState(false),
		[accountLabel, setAccountLabel] = useState(labels.selectAnAccount),
		[showEditAccountModal, setShowEditAccountModal] = useState(false),
		[isLoaderActive, setIsLoaderActive] = useState(false),
		[showAddNewAccountModal, setShowAddNewAccountModal] = useState(false),
		[createdAccountId, setCreatedAccountId] = useState(0),
		[isSelectNewAccount, setIsSelectNewAccount] = useState(false),
		[onAccountDropdownFocus, setOnAccountDropdownFocus] = useState(false);

	const availableAccounts = useMemo(() => {
		return _.orderBy(accountListOption, 'label', ['asc']);
	}, [accountListOption]);

	const accountsById = useSelector((state) => getSliceById(state, 'accounts')),
		engagementId = useEngagementId(),
		riskTypes = useSelector((store) => store.riskTypes);

	const dispatch = useDispatch();

	const allowedAccountTypes = [AccountType.Significant, AccountType.SignificantDisclosure];

	const handlerOnAccountItemChange = (account) => {
		setSelectedAccount([
			{
				value: account.value,
				label: account.label,
				title: account.title,
				typeId: account.typeId,
				statementTypeId: account.statementTypeId
			}
		]);
		setAccountLabel(labels.accountSelected);
		setIsAssertionsDropdownDisabled(false);
		setOnAccountDropdownFocus(false);
	};

	const handlerOnDeleteClick = () => {
		setIsLoading(true);
		dispatch(
			deleteAssertionRiskRelation(
				Number(engagementId),
				risk,
				selectedAssertions?.map((x) => x.id), //AssertionIds
				accountHash
			)
		).finally(() => {
			window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
			setIsLoading(false);
		});
	};

	const getAccountIdFromSelectedAccount = () => {
		return accounts.find((account) => {
			return account === selectedAccount[0].value;
		});
	};

	const updateNewlyCreatedAccount = (account) => {
		setCreatedAccountId(account.id);
		account.accountTypeId =
			isNull(account.accountTypeId) || !allowedAccountTypes.includes(account.accountTypeId)
				? AccountType.Significant
				: account.accountTypeId;
		const cgpspIndexes = [];
		account.pspIndexes?.forEach((item) => {
			const {cgpspIndexID} = item;
			cgpspIndexes.push(cgpspIndexID);
		});

		let accountAssertionsTypes = account.assertions?.map((assertion) => assertion.knowledgeAssertionId);
		if (selectedAssertions && selectedAssertions.length > 0) {
			selectedAssertions.map((assertion) => {
				if (!accountAssertionsTypes.includes(assertion.value)) {
					accountAssertionsTypes.push(assertion.value);
				}
			});
		}

		const uniqueAccountAssertionsTypes = [...new Set(accountAssertionsTypes)];

		return dispatch(
			updateAccount(engagementId, account.id, {
				accountName: account.accountName,
				accountType: account.accountTypeId,
				statementType: account.statementTypeId,
				hasEstimate: account.hasEstimate,
				statementTypeId: account.statementTypeId,
				executionType: account.executionTypeId,
				assertionTypes: uniqueAccountAssertionsTypes,
				cgpspIndexes: cgpspIndexes
			})
		);
	};

	const getNewlyCreatedAccountAssertions = useCallback(
		(accountId) => {
			const assertions = accountsById[accountId].assertions;
			const normalizeAssertions = normalize(assertions);
			return normalizeAssertions.byId;
		},
		[accountsById]
	);

	const onOptionCheckedHandler = (option) => {
		const accountId = getAccountIdFromSelectedAccount();
		let account = accountsById[accountId];
		let promise = Promise.resolve();
		setIsLoaderActive(true);
		if (option.isSelected) {
			let selected = selectedAssertions || [];
			selected.push(option);
			setSelectedAssertions(selected);
			promise = updateNewlyCreatedAccount(account);
			promise = promise.then(() => {
				const assertionId = accountsById[accountId].assertions.find((f) => {
					return f.knowledgeAssertionId === option.value;
				})?.id;
				option.id = assertionId;
				dispatch(
					createAssertionRiskRelation(
						Number(engagementId),
						risk,
						[
							{
								assertionId: assertionId
							}
						],
						[{assertionId: assertionId, account: accountsById[accountId]}],
						accountHash,
						getNewlyCreatedAccountAssertions(accountId)
					)
				).finally(() => {
					let _availableAssertionsBasedOnStatementType = availableAssertionsBasedOnStatementType.find(
						(x) => x.value === option.value
					);
					_availableAssertionsBasedOnStatementType.isSelected = option.isSelected;
					_availableAssertionsBasedOnStatementType.id = option.id;
				});
			});
		} else {
			let selected = selectedAssertions.filter((x) => x.id !== option.id);
			setSelectedAssertions(selected);
			promise = dispatch(deleteAssertionRiskRelation(Number(engagementId), risk, [option.id], accountHash));
		}
		promise.finally(() => {
			//Seems that server side doesnt complete the db operations, giving it a second to get the validations correctly
			setTimeout(() => {
				window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
			}, 1000);
			setIsLoaderActive(false);
		});

		return promise;
	};

	const openEditAccountModalHandler = () => {
		setShowEditAccountModal(true);
	};

	const showHideAccountsModal = () => {
		setShowEditAccountModal(!showEditAccountModal);
	};

	const handleOnEditAccountClose = () => {
		// Reload section
		getBodiesBySectionId();
	};

	const onFocus = useCallback(() => {
		// Calculate which accounts to show each time user clicks on the drop down
		let currentRiskType = riskTypes.data.find((riskType) => riskType.id === risk.riskTypeId);
		if (currentRiskType && currentRiskType.risks.length > 0) {
			let availableAccounts = [];
			currentRiskType.risks.map((r) => {
				let assertions = risk?.assertions;

				let notAvailableAccountsIds = assertions
					?.map((assertion) => assertion.account.id)
					?.filter((value, index, self) => self.indexOf(value) === index);

				// Do not show in the dropdown any accounts that have already been selected for that risk
				let available = accountListOption.filter(
					// show accounts that are significant or account dont have account type (Newly created account via dropdown link)
					(account) => !notAvailableAccountsIds?.includes(account.value)
				);

				// The selected account should be in the dropdown
				const found = selectedAccount && available.find((a) => a.value === selectedAccount[0].value);

				if (selectedAccount?.length > 0 && !found) {
					availableAccounts.push(selectedAccount[0]);
				} else {
					availableAccounts = available;
				}

				// Finally order the available accounts by label in ascending order.
				availableAccounts = _.orderBy(availableAccounts, 'label', ['asc']);
			});
		}
	}, [accountsById]);

	const getAccountOptionLabel = (params) => {
		return (
			accountsById[params.value] && (
				<section>
					<span>
						<EllipsesControl content={params.label} tooltip={params.label} noOfLines={1} />
					</span>
					<AccountExecutionType executionTypeId={accountsById[params.value].executionTypeId} />
				</section>
			)
		);
	};

	useEffect(() => {
		if (accountId && accounts?.length > 0) {
			let findAccount = accountsById[accountId];
			if (findAccount) {
				setSelectedAccount([
					{
						value: findAccount.id,
						label: findAccount.accountName,
						title: findAccount.accountName,
						statementTypeId: findAccount.statementTypeId
					}
				]);

				setSelectedStatementType(findAccount.statementTypeId);
				setAccountLabel(labels.accountSelected);
				setIsAccountDropdownDisabled(true);
				setIsAssertionsDropdownDisabled(false);
				setIsDeleteDisabled(false);
			}
		}
	}, [accountId, accounts]);

	useEffect(() => {
		let account = accountsById[accountId];
		if (!account) {
			account = accountsById[selectedAccount?.[0]?.value];
		}
		if (account) {
			setSelectedStatementType(account.statementTypeId);

			let availableAssertions = [];
			let allAssertions = [];
			currentResource.assertions
				.filter((assertion) => assertion.statementtypeid === account.statementTypeId)
				.map((assertion) => {
					allAssertions.push({
						id: assertion.id,
						value: assertion.id,
						label: assertion.assertionname,
						title: assertion.assertionname,
						assertionAbbreviation: assertion.assertionabbreviation,
						isSelected: false
					});
				});
			if (allAssertions.length > 0) {
				let selected = selectedAssertions || [];
				allAssertions.map((assertion) => {
					const riskAssertions = risk?.assertions.filter((x) => x.knowledgeAssertionId === assertion.value);
					const selectedAssertion = riskAssertions?.find((riskAssertion) => riskAssertion.account.id === account.id);
					assertion.isSelected = Boolean(selectedAssertion);
					if (selectedAssertion && assertion.isSelected) {
						assertion.id = selectedAssertion.id;
						if (!selected.find((x) => x.id === assertion.id || x.value === assertion.value)) {
							selected.push(assertion);
						}
					}
					availableAssertions.push(assertion);
				});
				setSelectedAssertions(selected);
			}
			setAvailableAssertionsBasedOnStatementType(availableAssertions);
		}
	}, [accountsById, selectedAccount, accountId]);

	const selectedAccountId = selectedAccount?.length > 0 && selectedAccount[0].value;

	useEffect(() => {
		const allAccount = Object.values(accountsById);
		const maxId = Math.max(...allAccount.map((f) => f.id));
		const createdAccount = allAccount.find((f) => f.accountTypeId === null && f.id === maxId);
		if (isSelectNewAccount && createdAccount && !showAddNewAccountModal) {
			let newAccount = {
				value: createdAccount.id,
				label: createdAccount.accountName,
				title: createdAccount.accountName,
				typeId: createdAccount.accountTypeId,
				statementTypeId: createdAccount.statementTypeId
			};

			availableAccounts.push(newAccount);
			setSelectedStatementType(newAccount.statementTypeId);
			handlerOnAccountItemChange(newAccount);
			setIsSelectNewAccount(false);
		}
	}, [accountsById, isSelectNewAccount]);

	const handleOnNewAccountClose = () => {
		setIsSelectNewAccount(true);
		setShowAddNewAccountModal(false);
	};

	const onNewAccountClick = useCallback(() => {
		setShowAddNewAccountModal(true);
	}, []);

	const renderFooter = useMemo(() => {
		return (
			<section className="newAccount-wrapper">
				<Button
					variant="text-alt"
					className="new-account"
					onClick={onNewAccountClick}
					showLabel
					isDisabled={isDisabled}
					buttonLabel={labels.newAccountAndDisclosure}
				/>
			</section>
		);
	});

	return (
		<>
			<StyledAccountListItem className="StyledAccountListItem">
				<ProgressBar isIndeterminate hideLabel circle className={isLoading ? 'account-loader' : 'hide'} />
				<SelectDropdown
					className={'accounts-dropdown'}
					formLabel={accountLabel}
					isSearchable
					id="accounts"
					noOptionsMessage={() => labels.noAccountsHaveBeenIdentified}
					isClearable={false}
					isDisabled={isAccountDropdownDisabled || isDisabled}
					value={selectedAccount}
					formatOptionLabel={getAccountOptionLabel}
					onChange={handlerOnAccountItemChange}
					options={availableAccounts}
					onFocus={onFocus}
					defaultValue={selectedAccount}
					footer={renderFooter}
					openMenuOnFocus={onAccountDropdownFocus}
					onMenuOpen={() => {
						setOnAccountDropdownFocus(true);
					}}
				/>
				<section className="statement-type">
					<span className="statementtype-label">{labels.statementType} </span>
					{selectedStatementType ? (
						<span className="statementtype-name">{currentResource.statementTypes[selectedStatementType - 1].name}</span>
					) : (
						<span className="statementtype-name">-</span>
					)}
				</section>
				<AssertionSelect
					accountId={selectedAccountId}
					showLoader={isLoaderActive}
					options={availableAssertionsBasedOnStatementType}
					onOptionChecked={onOptionCheckedHandler}
					isDisabled={isAssertionsDropdownDisabled || isDisabled}
					isFooterEnabled
					openEditAccountModalHandler={openEditAccountModalHandler}
				/>
				<IconButton
					type="button"
					variant="text"
					title={labels.delete}
					onClick={handlerOnDeleteClick}
					disabled={isDeleteDisabled || isDisabled}
					className="del-account"
					iconSource={actionIcDelete24px}
				/>
				{showEditAccountModal && !isDisabled && (
					<CreateEditAccountModalWrapper
						isEdit
						showModal={showEditAccountModal}
						setShowModal={showHideAccountsModal}
						accountId={selectedAccountId}
						onClose={handleOnEditAccountClose}
					/>
				)}
				{showAddNewAccountModal && !isDisabled && (
					<CreateEditAccountModalWrapper
						isCreate
						createMinimalAccount
						showModal={showAddNewAccountModal}
						setShowModal={setShowAddNewAccountModal}
						onClose={handleOnNewAccountClose}
					/>
				)}
			</StyledAccountListItem>
		</>
	);
}

const StyledAccountListItem = styled.section`
	margin-bottom: var(--px-24);
	position: relative;
	.motif-progress-bar-wrapper {
		&.account-loader {
			position: absolute;
			width: 100%;
			.motif-progress-bar {
				width: var(--px-20);
				height: var(--px-20);
			}
		}
	}
	.accounts-dropdown {
		display: inline-block;
		width: 28%;
		padding-right: var(--px-50);
		vertical-align: bottom;
		.ellipses {
			width: auto;
		}
	}
	.statement-type {
		display: inline-block;
		width: 32%;
		padding-right: var(--px-64);
		height: var(--px-44);
		vertical-align: bottom;
		.statementtype-label {
			display: block;
			width: 100%;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			font-weight: 400;
			font-size: var(--px-16);
			line-height: var(--px-24);
			color: var(--neutrals-900);
		}
		.statementtype-name {
			display: block;
			width: 100%;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			font-weight: 300;
			font-size: var(--px-14);
			line-height: var(--px-20);
			color: var(--neutrals-700);
		}
	}
	.assertions-dropdown {
		display: inline-block;
		width: 100%;
		vertical-align: bottom;
		&.select-disabled {
			cursor: default;
		}
	}
	.del-account {
		display: inline-block;
		width: 5%;
		vertical-align: super;
		&:hover,
		&:focus {
			background-color: transparent;
		}
		.motif-icon {
			float: right;
		}
	}
	.newAccount-wrapper {
		background: var(--neutrals-00white);
		border-top: var(--px-1) solid var(--neutrals-300);
		border-bottom-left-radius: var(--px-8);
		border-bottom-right-radius: var(--px-8);
		.new-account {
			margin: var(--px-12) var(--px-14);
			line-height: var(--px-20);
		}
	}
	&.StyledAccountListItem {
		display: flex;
		align-items: center;
		.statement-type {
			width: 15%;
			margin-left: var(--px-64);
			height: var(--px-44);
		}

		.del-account {
			display: flex;
			align-items: center;
			margin-left: var(--px-24);
			width: 3%;
			justify-content: flex-start;
		}

		.accounts-dropdown {
			padding-right: 0;
			width: 40%;
			.react-select__menu {
				@media only screen and (max-width: 1280px) {
					margin-left: calc(var(--px-2) * -1) !important;
					width: calc(100% + var(--px-3)) !important;
				}
			}
		}

		.assertions-wrapper {
			width: 35%;
			position: relative;

			.react-select-container {
				.react-select__control {
					@media only screen and (max-width: 1280px) {
						min-height: var(--px-55);
					}
				}
				.react-select__value-container--is-multi {
					@media only screen and (max-width: 1280px) {
						height: var(--px-26) !important;
					}
				}
			}

			.assertions-dropdown {
				.react-select__menu-list {
					.react-select__option {
						&:last-child {
							border-radius: 0;
						}
					}
				}
				.editaccount-wrapper {
					.edit-account {
						border-bottom: 0;
						line-height: var(--px-20);
						&:hover {
							text-decoration: underline;
						}
					}
				}
			}
		}
	}
`;
